<template>
  <div class="rights-section">
    <!-- 图层顺序：绿色渐变块(底) → 磨砂玻璃层 → 标题/表格(顶)
         green 块在玻璃后面，经玻璃 backdrop-filter 模糊后向下渐淡，即设计稿效果 -->
    <i class="rs-glow"></i>
    <div class="rs-glass"></div>
    <div class="rs-glass-stroke"></div>
    <div class="rs-head">
      <span class="rs-title">{{ title }}</span>
      <span v-if="showMore" class="rs-more" @click="$emit('more')">
        {{ moreText }}<img class="rs-arrow" :src="arrowIcon" alt="" />
      </span>
    </div>
    <div class="rs-body">
      <rights-table :rights="rights" />
    </div>
  </div>
</template>

<script>
import RightsTable from "./RightsTable.vue";
import { PRO_PLAN_TEXT } from "@/consts/proPlan";

/**
 * 专业版权益区块（套餐购买页 / 专业版权益页 共用）
 * 结构对应设计稿 group #1882:2099：左上绿色渐变块 + 白色玻璃卡 + 权益对比表
 */
export default {
  name: "RightsSection",
  components: { RightsTable },
  props: {
    rights: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: () => PRO_PLAN_TEXT.rightsTableTitle,
    },
    showMore: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      moreText: PRO_PLAN_TEXT.viewRightsDetail,
      arrowIcon: require("@/assets/images/pro-plan/plan/plan-arrow.png"),
    };
  },
};
</script>

<style lang="scss" scoped>
.rights-section {
  position: relative;
  width: 1100px;
  margin: 0 auto;
  padding: 11px 0 20px;
  box-sizing: border-box;
  border-radius: 20px;
  overflow: hidden;
}
// 磨砂玻璃层（设计稿 IMAGE-SVG #1882:2101）
// 真实毛玻璃：backdrop-filter 模糊其下的绿色渐变块，白色半透明渐变提供磨砂底色；
// 形状（含左上角缺口凹槽）用 SVG 作 mask 裁剪，曲线由贝塞尔保证圆滑（clip-path 折线不够圆滑）。
.rs-glass {
  position: absolute;
  left: 0;
  top: 11px;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.82) 0%,
    rgba(255, 255, 255, 0.6) 65%
  );
  backdrop-filter: blur(23.05px);
  -webkit-backdrop-filter: blur(23.05px);
  -webkit-mask-image: url("~@/assets/images/pro-plan/plan/rights-glass-mask.png");
  mask-image: url("~@/assets/images/pro-plan/plan/rights-glass-mask.png");
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
}
// 玻璃描边（单独一层，避免与磨砂层白色渐变重复叠加导致过白）
.rs-glass-stroke {
  position: absolute;
  left: 0;
  top: 11px;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
  background: url("~@/assets/images/pro-plan/plan/rights-glass-stroke.png")
    no-repeat;
  background-size: 100% 100%;
}
// 左上角绿色渐变块（设计稿 RECTANGLE #1882:2100），位于玻璃之下，从玻璃缺口处露出。
//
// ⚠️ 青色叠加层的透明度：设计稿写的是 rgba(99,255,237,1)（不透明），但那是 Figma 的
// 「描边」渐变 —— 描边只作用于图形边界（约 2px 的细线），并不铺满整个矩形。
// 直接拿它当填充色会整块泛白（左上角被冲成 90,252,227，设计稿是 39,241,176）。
// 按实际渲染结果反推，等效透明度约 0.35。
// 实测（同区域 3920 个采样点，与设计稿的累计色差）：
//   alpha 1（照抄稿值）19.8 < alpha 0.5 16.0 ≈ 无青色 16.2 < **alpha 0.35 15.4**（最优）
.rs-glow {
  position: absolute;
  left: 0;
  top: 0;
  width: 235px;
  height: 92px;
  z-index: 0;
  border-radius: 20px;
  pointer-events: none;
  background: linear-gradient(
      -11deg,
      rgba(36, 245, 196, 0) 56%,
      rgba(99, 255, 237, 0.35) 100%
    ),
    linear-gradient(177deg, rgba(0, 229, 122, 1) 0%, rgba(15, 168, 123, 1) 55%);
}
.rs-head {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  // 右侧 51px：设计稿（1882:2099）「查看权益详情」+ 箭头的右沿距区块右边 51px
  // （文字 x=908 宽 119、箭头 x=1031 宽 18 → 右沿 1049，区块宽 1100）。
  // 原先 27px 会让整组偏右 24px（禅道 Bug#23428-4）
  padding: 0 51px 0 10px;
  // 设计稿：标题带区域高约 61px，表格顶边距区块顶 72px
  margin-bottom: 20px;
}
.rs-title {
  font-family: "Alimama ShuHeiTi", "Microsoft YaHei";
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  color: #fff;
}
.rs-more {
  display: inline-flex;
  align-items: center;
  margin-top: 14px;
  font-family: "Alibaba PuHuiTi 2.0", "Microsoft YaHei";
  font-size: 20px;
  font-weight: 500;
  line-height: normal;
  color: #0fa87b;
  cursor: pointer;
}
.rs-arrow {
  width: 18px;
  height: 18px;
  margin-left: 4px;
  margin-top: 2px;
  display: block;
}
.rs-body {
  position: relative;
  z-index: 2;
  padding: 0 27px;
}
</style>
